  <!--
    Usage :
    <FuzzySearchList :authenticated="authenticated===true" :initialLocations="buildingLocations" :fullLocations="buildingLocations" :aliasLocations="aliasLocations"
      searchPlaceholder="Search for a building..." :buildingId="buildingId" :toId="toId" :toType="toType"/>
  -->
  
<template>
  <Spinner ref="spinner" />

  <Survey />

  <div id="search-box"
    class="d-flex align-items-center gap-3 rounded-pill px-3 py-1 my-input-group m-2 w-auto search-gray">
    <font-awesome-icon id="search-icon" icon="search" size="sm" />
    <input v-model="searchPattern" type="text" :placeholder="props.searchPlaceholder" autocomplete="off"
      id="search-input" ref="searchInput" class="form-control border-0 shadow-none mt-1 w-100" data-test="search-input">
  </div>

  <div class="list-group m-2">
    <SearchResult v-for="result in results.slice(0, refineMessageIndex)" :result="result" :url="getUrl(result.item.locationId)" data-test="result"/>

    <div v-if="!isInitialResults" class="refineMessage d-flex align-items-center gap-2">
      <font-awesome-icon icon="question-circle"></font-awesome-icon>
      <p v-if="results.length >= refineMessageIndex" class="fw-bold mb-0">Can't find what your looking for? Try searching more words, different spellings, or a different name for the room/building.</p>
      <p v-if="results.length < refineMessageIndex" class="fw-bold mb-0">Can't find what your looking for? Try searching less words, different spellings, or a different name for the room/building.</p>
    </div>

    <SearchResult v-for="result in results.slice(refineMessageIndex)" :result="result" :url="getUrl(result.item.locationId)"/>
  </div>
</template>

<script setup>
import { onMounted, ref, watch } from 'vue'
import Spinner from '@/components/Spinner.vue'
import { useRoute } from 'vue-router'
import { useMapCommon } from '../../composables/MapCommon.js'
import Fuse from 'fuse.js'
import SearchResult from '../../components/map/SearchResult.vue'
import Survey from '../../components/Survey.vue'

const route = useRoute()

const initialLocations = ref([])
const locationsMap = ref([]) // map with location ids as keys and official names as values
const searchLocations = ref([]) // list of all names for locations including aliases and their ids
const results = ref([]) // filtered list displayed to user

const isInitialResults = ref(true) // stops "refine your search" message appearing in initial list
const refineMessageIndex = 30

const { urlQueryPersist } = useMapCommon()

const spinner = ref(null)
const searchInput = ref(null)
const searchPattern = ref("")
let fuse

const props = defineProps({
  authenticated: Boolean,
  // Location object of the form `{locationId: 'x', locationName: 'y'}`. when we switch to TypeScript this should be in code
  initialLocations: Array, // the list shown at the beginning before the search filter is changed
  fullLocations: Array, // the list of all real locations
  aliasLocations: Array, // the list of aliases
  searchPlaceholder: String,
  // optional, added to urls if included
  buildingId: String,
  toId: String,
  toType: String
})

onMounted(async () => {
  spinner.value?.spin()
  searchInput.value?.focus()
})

// initialise search when data is passed in
watch(
  () => [props.initialLocations, props.fullLocations, props.aliasLocations],
  ([initialLocationsProp, fullLocations, aliasLocations]) => {
    if(initialLocationsProp != null && fullLocations != null && aliasLocations != null) {
      //Set up variables
      const aliasMap = aliasLocations.reduce((map, { locationId, locationName }) => {
        if (!map.has(locationId)) map.set(locationId, []);
        map.get(locationId).push(locationName);
        return map;
      }, new Map());

      searchLocations.value = fullLocations.map(({ locationId, locationName }) => ({
        locationId,
        locationName,
        locationAliases: aliasMap.get(locationId) ?? [],
      }));

      initialLocations.value = initialLocationsProp
      initialLocations.value = initialLocations.value
      .filter(location => location.locationId !== props.toId) // don't include the building being navigated to in the results
      .map(loc => {return {item: loc, displayName: loc.locationName}}) // wrangle into fuse format
      .sort((location, location2) => location.item.locationName.localeCompare(location2.item.locationName, "en"));
      results.value = initialLocations.value

      // set up fuzzy search
      fuse = new Fuse(searchLocations.value,
        { keys: [{name: "locationName", weight: 1}, {name: "locationAliases", weight: 0.5}],
        threshold: 0.5,
        includeMatches: true
        }
      )
      spinner.value?.stop()
    }
  },
  {immediate: true}
)

/**
 * get the directions url for a given id
 * @param id a location id (e.g. 173 for James McCune Smith Learning Hub)
 */
function getUrl(id) {
  let fromId
  let url
  let urlParams = {buildingId: 'buildingId=', toId: '&toId=', toType: '&toType=', fromId: '&fromId=', fromType: '&fromType='}

  // if toId is populated already, passed in id must be for fromId, and we are linking directions
  if (props.toId) {
    fromId = id
    id = props.toId

    url ='directions?'
    + urlParams.buildingId + props.buildingId
    + urlParams.toId + props.toId
    + urlParams.toType + props.toType
    + urlParams.fromId + fromId
    + urlParams.fromType + roomOrBuilding(fromId)
  } else { // else we are linking a map location
    url = 'map?'
    + urlParams.buildingId + id.substr(0, 3)
    + urlParams.toId + id
    + urlParams.toType + roomOrBuilding(id)
  }

  //Add any url query parameters
  url = urlQueryPersist(url, route)

  return url
}

function roomOrBuilding(id) {
  return id.length > 3 ? 'R' : 'B'
}

/**
 * when search pattern updates, filter the results by it
 */
watch(searchPattern, pattern => {
  if (!fuse || !searchLocations.value.length) return

  if (pattern === '') {
    results.value = initialLocations.value
    isInitialResults.value = true
    return
  }

  results.value = fuse.search(pattern)
    .map(result => {
      const nameMatch = result.matches.find(m => m.key === 'locationName')

      // find which match is best (there are more efficient approaches but none as accurate)
      const bestMatch = new Fuse(
        result.matches,
        {keys: [{name: 'value', weight: 1}]}
      ).search(pattern, {limit: 1})[0].item
      const isBestAlias = bestMatch !== result.item.locationName

      if (nameMatch) {
        result.displayName = highlight(
          result.item.locationName,
          nameMatch.indices
        )
        result.displayAlias = null
      } else if (isBestAlias) {
        result.displayName = result.item.locationName
        result.displayAlias = highlight(
          bestMatch.value,
          bestMatch.indices
        )
      }

      return result
    })
    .filter(result => result.item.locationId !== props.toId) // don't include the building being navigated to in the results

  isInitialResults.value = false
})

// sorts set of indices returned by fuse as they can overlap and mince room names
function getNonOverlappingIndices(originalIndices) {
    const indices = [...originalIndices];
    // Sort the sets by the first element of each set
    indices.sort((a, b) => a[0] - b[0]);

    let result = [indices[0]];

    for (let i = 1; i < indices.length; i++) {
        // Get the last set in the result array
        let lastSet = result[result.length - 1];

        // If the current set does not overlap with the last set, add it to the result
        if (indices[i][0] > lastSet[1]) {
            result.push(indices[i]);
        }
    }

    return result;
}

function highlight(text, overlappingIndices) {
  const indices = getNonOverlappingIndices(overlappingIndices)

  if (!indices?.length) return text

  let result = ''
  let last = 0

  indices.forEach(([start, end]) => {
    result += text.slice(last, start)
    result += `<strong>${text.slice(start, end + 1)}</strong>`
    last = end + 1
  })

  result += text.slice(last)
  return result
}
</script>

<style>
.refineMessage {
  color: var(--uog-dark-blue);
  font-family: PTSans;
  height: fit-content;
  line-height: normal;
  background-color: var(--uog-blue-10);
  border-radius: 5px;
  padding: 8px;
  border-top: none;
  margin-left: 24px;
  margin-right: 24px;
}

#search-box {
  background-color: #f7f7f7;
}

#search-icon {
  color: var(--uog-dark-blue);
}

#search-input {
  color: var(--uog-dark-blue);
  background-color: #f7f7f7;
}
</style>
